import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Polygon, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { FiMapPin, FiX, FiMap, FiGrid, FiLayers, FiInfo } from 'react-icons/fi';
import api from '../services/api';

// Fix Leaflet default icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface Feature {
  numero: string;
  coordinates: [number, number][];
  centroid: [number, number];
  superficie: number;
  isMine: boolean;
  attribuee: boolean;
  attribuable: boolean;
  attributaire_nom: string | null;
}

interface ProjetData {
  id: string;
  libelle: string;
  localisation: string;
  mesParcelles: any[];
  features: Feature[];
  stats: { total: number; miennes: number };
}

function FitBounds({ features }: { features: Feature[] }) {
  const map = useMap();
  useEffect(() => {
    if (features.length > 0) {
      const allCoords = features.flatMap(f => f.coordinates);
      if (allCoords.length > 0) {
        const bounds = L.latLngBounds(allCoords.map(c => [c[0], c[1]] as [number, number]));
        map.fitBounds(bounds, { padding: [30, 30] });
      }
    }
  }, [features, map]);
  return null;
}

function FlyToFeature({ feature }: { feature: Feature | null }) {
  const map = useMap();
  useEffect(() => {
    if (feature) {
      map.flyTo([feature.centroid[0], feature.centroid[1]], 20, { duration: 0.8 });
    }
  }, [feature, map]);
  return null;
}

const MaCartographie = () => {
  const [data, setData] = useState<{ projets: ProjetData[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedProjet, setSelectedProjet] = useState('');
  const [selectedParcelle, setSelectedParcelle] = useState<Feature | null>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [flyTarget, setFlyTarget] = useState<Feature | null>(null);

  useEffect(() => {
    api.get('/cartographie/mes-parcelles')
      .then(r => {
        setData(r.data);
        if (r.data.projets?.length > 0) {
          setSelectedProjet(r.data.projets[0].id);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading">Chargement...</div>;

  if (!data || data.projets.length === 0) {
    return (
      <div>
        <h1 className="page-title"><FiMapPin /> Ma Cartographie</h1>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
            <FiMap style={{ fontSize: '3rem', color: '#ccc', marginBottom: '1rem' }} />
            <p style={{ color: '#888', fontSize: '1.1rem' }}>Aucune parcelle attribuée pour le moment.</p>
          </div>
        </div>
      </div>
    );
  }

  const currentProjet = data.projets.find(p => p.id === selectedProjet);
  const features = currentProjet?.features || [];
  const mesParcelles = currentProjet?.mesParcelles || [];
  const mesNumeros = new Set(mesParcelles.map(p => p.numero));

  const getColor = (f: Feature) => {
    if (selectedParcelle && f.numero === selectedParcelle.numero) return '#ff6600';
    if (f.isMine) return '#0d47a1'; // Dark blue - my parcels
    if (!f.attribuable) return '#d5d5d5'; // Light gray
    if (f.attribuee) return '#b0bec5'; // Blue-gray
    return '#e0e0e0'; // Light gray
  };

  const getStyle = (f: Feature) => ({
    color: f.isMine ? '#0a3d91' : '#999',
    weight: f.isMine ? 3 : (selectedParcelle?.numero === f.numero ? 3 : 0.8),
    fillColor: getColor(f),
    fillOpacity: f.isMine ? 0.7 : 0.2,
  });

  const handlePolygonClick = (f: Feature) => {
    setSelectedParcelle(f);
    setShowPopup(true);
  };

  const handleLocateParcelle = (parcelle: any) => {
    const feature = features.find(f => f.numero === parcelle.numero);
    if (feature) {
      setSelectedParcelle(feature);
      setFlyTarget(feature);
      setShowPopup(true);
    }
  };

  const openGoogleMapsDirections = (centroid: [number, number]) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${centroid[0]},${centroid[1]}&travelmode=driving`;
    window.open(url, '_blank');
  };

  const openGoogleMapsSituation = (centroid: [number, number]) => {
    const url = `https://www.google.com/maps/@${centroid[0]},${centroid[1]},18z/data=!3m1!1e1`;
    window.open(url, '_blank');
  };

  return (
    <div className="cartographie-page">
      <h1 className="page-title"><FiMapPin /> Ma Cartographie</h1>

      {/* Project selector if member has multiple projects */}
      {data.projets.length > 1 && (
        <div className="carto-toolbar" style={{ marginBottom: 12 }}>
          <div className="carto-toolbar-left">
            <div className="form-group" style={{ marginBottom: 0, minWidth: 220 }}>
              <select className="form-input" value={selectedProjet} onChange={e => { setSelectedProjet(e.target.value); setSelectedParcelle(null); setShowPopup(false); }}>
                {data.projets.map(p => (
                  <option key={p.id} value={p.id}>{p.libelle} ({p.mesParcelles.length} parcelle(s))</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      {currentProjet && (
        <div className="carto-stats" style={{ marginBottom: 12 }}>
          <div className="carto-stat">
            <FiLayers />
            <span><strong>{currentProjet.stats.total}</strong> parcelles au total</span>
          </div>
          <div className="carto-stat" style={{ color: '#c6a531' }}>
            <FiGrid />
            <span><strong>{currentProjet.stats.miennes}</strong> parcelle(s) attribuée(s)</span>
          </div>
          {currentProjet.localisation && (
            <div className="carto-stat">
              <FiMapPin />
              <span>{currentProjet.localisation}</span>
            </div>
          )}
        </div>
      )}

      {/* Map + Info */}
      <div className="carto-container">
        <div className={`carto-map-wrapper ${showPopup && selectedParcelle ? 'with-panel' : ''}`}>
          {features.length === 0 ? (
            <div className="carto-empty">
              <FiMap size={48} />
              <p>Aucune donnée cartographique disponible pour ce projet</p>
            </div>
          ) : (
            <MapContainer
              center={[14.829, -17.195]}
              zoom={18}
              style={{ height: '100%', width: '100%', borderRadius: 8 }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/">OSM</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution="&copy; Esri"
              />
              <FitBounds features={features} />
              <FlyToFeature feature={flyTarget} />

              {features.map(f => (
                <Polygon
                  key={f.numero}
                  positions={f.coordinates}
                  pathOptions={getStyle(f)}
                  eventHandlers={{
                    click: () => handlePolygonClick(f),
                    mouseover: (e) => {
                      e.target.setStyle({ fillOpacity: f.isMine ? 0.7 : 0.35, weight: 2.5 });
                      const label = f.isMine
                        ? `<strong style="color:#0d47a1">★ Ma parcelle ${f.numero}</strong>`
                        : `<span style="color:#999">Parcelle ${f.numero}</span>`;
                      e.target.bindTooltip(label, { sticky: true }).openTooltip();
                    },
                    mouseout: (e) => {
                      e.target.setStyle(getStyle(f));
                      e.target.unbindTooltip();
                    }
                  }}
                />
              ))}
            </MapContainer>
          )}
        </div>

        {/* Info Panel */}
        {showPopup && selectedParcelle && (
          <div className="carto-info-panel">
            <div className="carto-info-header">
              <h3>{selectedParcelle.isMine ? 'Ma parcelle' : 'Parcelle'} {selectedParcelle.numero}</h3>
              <button className="carto-info-close" onClick={() => { setShowPopup(false); setSelectedParcelle(null); }}><FiX /></button>
            </div>
            <div className="carto-info-body">
              {selectedParcelle.isMine && (
                <div className="mc-badge-mine">
                  <FiGrid /> Votre parcelle
                </div>
              )}

              {/* Find matching parcelle data */}
              {(() => {
                const mp = mesParcelles.find(p => p.numero === selectedParcelle.numero);
                return (
                  <>
                    <div className="carto-info-row">
                      <label>Superficie</label>
                      <span>{selectedParcelle.superficie ? `${selectedParcelle.superficie.toLocaleString('fr-FR')} m²` : '-'}</span>
                    </div>
                    {mp?.lot && (
                      <div className="carto-info-row">
                        <label>Lot</label>
                        <span>{mp.lot}</span>
                      </div>
                    )}
                    {mp?.nicad && (
                      <div className="carto-info-row">
                        <label>NICAD</label>
                        <span>{mp.nicad}</span>
                      </div>
                    )}
                    {mp?.numtf && (
                      <div className="carto-info-row">
                        <label>N° Titre Foncier</label>
                        <span>{mp.numtf}</span>
                      </div>
                    )}
                    <div className="carto-info-row">
                      <label>Coordonnées</label>
                      <span style={{ fontSize: '0.78rem' }}>{selectedParcelle.centroid[0].toFixed(6)}, {selectedParcelle.centroid[1].toFixed(6)}</span>
                    </div>
                  </>
                );
              })()}

              <div className="carto-info-divider" />

              {/* Action buttons */}
              <div className="mc-actions">
                <button
                  className="btn btn-primary btn-block"
                  onClick={() => openGoogleMapsSituation(selectedParcelle.centroid)}
                >
                  <FiMap /> Situation du terrain
                </button>
                <button
                  className="btn btn-success btn-block"
                  onClick={() => openGoogleMapsDirections(selectedParcelle.centroid)}
                >
                  <FiMapPin /> Itinéraire pour y aller
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="carto-legend">
        <span className="carto-legend-item"><span className="carto-legend-color" style={{ background: '#0d47a1' }} /> Mes parcelles</span>
        <span className="carto-legend-item"><span className="carto-legend-color" style={{ background: '#b0bec5' }} /> Autres parcelles attribuées</span>
        <span className="carto-legend-item"><span className="carto-legend-color" style={{ background: '#e0e0e0' }} /> Non attribuées</span>
        <span className="carto-legend-item"><span className="carto-legend-color" style={{ background: '#ff6600' }} /> Sélectionnée</span>
      </div>

      {/* My parcels list */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header">
          <span>Mes parcelles ({mesParcelles.length})</span>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>N°</th>
                  <th>Projet</th>
                  <th>Superficie</th>
                  <th>Lot</th>
                  <th>NICAD</th>
                  <th>N° TF</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {mesParcelles.map(p => (
                  <tr key={p.id}>
                    <td><strong>{p.numero}</strong></td>
                    <td>{p.projet_libelle || '-'}</td>
                    <td>{p.superficie ? `${p.superficie.toLocaleString('fr-FR')} m²` : '-'}</td>
                    <td>{p.lot || '-'}</td>
                    <td>{p.nicad || '-'}</td>
                    <td>{p.numtf || '-'}</td>
                    <td>
                      <div className="actions">
                        <button
                          className="btn-icon btn-info"
                          title="Localiser sur la carte"
                          onClick={() => handleLocateParcelle(p)}
                        >
                          <FiMapPin />
                        </button>
                        <button
                          className="btn-icon btn-primary"
                          title="Situation du terrain"
                          onClick={() => {
                            const f = features.find(ft => ft.numero === p.numero);
                            if (f) openGoogleMapsSituation(f.centroid);
                          }}
                        >
                          <FiMap />
                        </button>
                        <button
                          className="btn-icon btn-success"
                          title="Itinéraire"
                          onClick={() => {
                            const f = features.find(ft => ft.numero === p.numero);
                            if (f) openGoogleMapsDirections(f.centroid);
                          }}
                        >
                          <FiMap />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {mesParcelles.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: 'center' }}>Aucune parcelle</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaCartographie;
